(() => {
  'use strict';

  // =========================
  // Ayarlar
  // =========================
  const CONFIG = {
    // Koleksiyon/arama sayfasında ürün başına JSON çekilecek; çok kalabalık sayfalarda yükü azaltmak için sınır:
    listingMaxProductsToProcess: 36,

    // Bir varyant ismi birden çok yerde geçebildiği için aramayı "tam eşleşme" yapıyoruz:
    matchMode: 'exact', // 'exact' | 'contains'
  };

  // =========================
  // Yardımcılar
  // =========================
  const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

  const normalize = (s) =>
    (s || '')
      .toString()
      .trim()
      .replace(/\s+/g, ' ')
      .toLowerCase();

  const textMatches = (candidate, target) => {
    const c = normalize(candidate);
    const t = normalize(target);
    if (!c || !t) return false;
    return CONFIG.matchMode === 'contains' ? c.includes(t) : c === t;
  };

  const uniq = (arr) => Array.from(new Set(arr));

  const getProductHandleFromUrl = () => {
    // /products/handle veya /products/handle?variant=...
    const m = window.location.pathname.match(/^\/products\/([^\/]+)/);
    return m ? m[1] : null;
  };

  async function fetchProductJsonByHandle(handle) {
    // Shopify ürün JSON endpoint: /products/{handle}.js
    const url = `/products/${encodeURIComponent(handle)}.js`;
    const res = await fetch(url, { credentials: 'same-origin' });
    if (!res.ok) throw new Error(`Product JSON fetch failed: ${url}`);
    return res.json();
  }

  function computeAlwaysSoldOutOptionValues(productJson) {
    // "Her durumda satılamayan" option değerlerini bul:
    // Örn: Renk=Haki olan tüm varyantlar available=false ise Haki gizlenecek.
    const variants = Array.isArray(productJson?.variants) ? productJson.variants : [];
    const options = Array.isArray(productJson?.options) ? productJson.options : [];

    // option index bazında map: value -> { anyAvailable: bool }
    const byOptionIndex = options.map(() => new Map());

    for (const v of variants) {
      const available = !!v.available;
      const optValues = [v.option1, v.option2, v.option3];

      optValues.forEach((val, idx) => {
        if (!val || !byOptionIndex[idx]) return;
        const key = normalize(val);
        if (!key) return;
        const current = byOptionIndex[idx].get(key) || { label: val, anyAvailable: false };
        current.label = current.label || val;
        current.anyAvailable = current.anyAvailable || available;
        byOptionIndex[idx].set(key, current);
      });
    }

    // Her option için: anyAvailable=false olanları gizlenecekler listesine al
    return byOptionIndex.map((map) => {
      const soldOutValues = [];
      for (const [, info] of map.entries()) {
        if (!info.anyAvailable) soldOutValues.push(info.label);
      }
      return uniq(soldOutValues);
    });
  }

  function hideMatchingElementsForValue(root, value) {
    if (!root || !value) return;

    const valueNorm = normalize(value);

    // 1) Select option
    root.querySelectorAll('option').forEach((opt) => {
      const txt = opt.textContent;
      const val = opt.getAttribute('value');
      if (textMatches(txt, valueNorm) || textMatches(val, valueNorm)) {
        // option'ı görünmez + seçilemez yap
        opt.disabled = true;
        opt.hidden = true;
        opt.style.display = 'none';
      }
    });

    // 2) Radio / checkbox input + label
    root.querySelectorAll('input[type="radio"], input[type="checkbox"]').forEach((inp) => {
      const val = inp.getAttribute('value');
      if (!textMatches(val, valueNorm)) return;

      // input görünmesin
      inp.disabled = true;

      // En yakın label / wrapper'ı gizle
      const label =
        inp.closest('label') ||
        root.querySelector(`label[for="${CSS.escape(inp.id || '')}"]`) ||
        inp.parentElement;

      if (label) label.style.display = 'none';
      else inp.style.display = 'none';
    });

    // 3) Button / swatch gibi elemanlar (data-value, aria-label, title)
    root.querySelectorAll('[data-value], [aria-label], [title]').forEach((el) => {
      const dv = el.getAttribute('data-value');
      const al = el.getAttribute('aria-label');
      const tt = el.getAttribute('title');

      if (textMatches(dv, valueNorm) || textMatches(al, valueNorm) || textMatches(tt, valueNorm)) {
        el.style.display = 'none';
        // tıklanmayı da engelle
        el.setAttribute('aria-hidden', 'true');
        el.style.pointerEvents = 'none';
      }
    });

    // 4) Son çare: yalın metin olarak görünen küçük seçenekler (örn: "Krem Haki" metinleri)
    // Çok agresif olmamak için sadece kısa ve "seçenek gibi" duran elemanlarda dener.
    root.querySelectorAll('button, a, span, div').forEach((el) => {
      if (el.children && el.children.length) return;
      const txt = el.textContent;
      if (!txt) return;

      // metin "Haki" ise ve bir seçim grubuna benziyorsa gizle
      if (textMatches(txt, valueNorm)) {
        // yakınında input/select/fieldset varsa daha güvenli
        const looksLikePicker =
          el.closest('fieldset, .product-form, form, .variant, .variants, .product__info, .product-info') ||
          el.querySelector('input, select');

        if (looksLikePicker) el.style.display = 'none';
      }
    });
  }

  function applyProductPageHiding(productJson) {
    const soldOutByOption = computeAlwaysSoldOutOptionValues(productJson);

    // Ürün sayfasında olası alanlar: form / product-info bölgesi
    const roots = [
      document.querySelector('main'),
      document.querySelector('form[action^="/cart/add"]'),
      document.querySelector('.product'),
      document.querySelector('.product__info'),
      document.body,
    ].filter(Boolean);

    soldOutByOption.flat().forEach((value) => {
      roots.forEach((r) => hideMatchingElementsForValue(r, value));
    });
  }

  function getListingProductHandles() {
    // Collection + Search gibi sayfalarda ürün linkleri: /products/{handle}
    const anchors = Array.from(document.querySelectorAll('a[href*="/products/"]'));
    const handles = anchors
      .map((a) => {
        const m = (a.getAttribute('href') || '').match(/\/products\/([^\/\?\#]+)/);
        return m ? m[1] : null;
      })
      .filter(Boolean);

    return uniq(handles).slice(0, CONFIG.listingMaxProductsToProcess);
  }

  async function applyListingPageHiding() {
    const handles = getListingProductHandles();
    if (!handles.length) return;

    // Aynı handle için tekrar fetch yapmamak için cache
    const cache = new Map();

    // DOM’da ürün kartı içinde geçen swatch/label metinlerini gizleyeceğiz:
    // (Tema farklı olabilir; bu yüzden genel çalışıyoruz.)
    for (const handle of handles) {
      try {
        let productJson = cache.get(handle);
        if (!productJson) {
          productJson = await fetchProductJsonByHandle(handle);
          cache.set(handle, productJson);
          // ufak throttle
          await sleep(60);
        }

        const soldOutByOption = computeAlwaysSoldOutOptionValues(productJson);
        const soldOutValues = soldOutByOption.flat();
        if (!soldOutValues.length) continue;

        // Bu ürünün kartlarını bul (aynı ürün sayfada birden fazla kez çıkabilir)
        const cardLinks = Array.from(
          document.querySelectorAll(`a[href*="/products/${CSS.escape(handle)}"]`)
        );

        const cardRoots = uniq(
          cardLinks
            .map((a) => a.closest('article, li, .card, .product-card, .grid__item, .collection, .product'))
            .filter(Boolean)
        );

        cardRoots.forEach((root) => {
          soldOutValues.forEach((v) => hideMatchingElementsForValue(root, v));
        });
      } catch (e) {
        // sessizce geç (diğer script'leri etkilemesin)
        // console.debug('[hide-soldout-variants] listing error', handle, e);
      }
    }
  }

  function isProductPage() {
    return /^\/products\/[^\/]+/.test(window.location.pathname);
  }

  function isCollectionPage() {
    return /^\/collections\/[^\/]+/.test(window.location.pathname);
  }

  function isSearchPage() {
    // Shopify arama: /search veya /search?q=...
    return /^\/search(\/)?$/.test(window.location.pathname);
  }

  async function init() {
    try {
      if (isProductPage()) {
        const handle = getProductHandleFromUrl();
        if (!handle) return;

        const productJson = await fetchProductJsonByHandle(handle);
        applyProductPageHiding(productJson);

        // Tema AJAX ile varyant UI’ı yeniden çiziyorsa tekrar uygula:
        const mo = new MutationObserver(() => applyProductPageHiding(productJson));
        mo.observe(document.body, { childList: true, subtree: true });
      }

      // Collection + Search = Listing mantığı
      if (isCollectionPage() || isSearchPage()) {
        await applyListingPageHiding();

        // Sonsuz scroll / filtre / canlı arama ile ürünler yeniden yükleniyorsa tekrar dene:
        const mo = new MutationObserver(() => {
          // küçük debounce
          clearTimeout(window.__hsov_t);
          window.__hsov_t = setTimeout(() => applyListingPageHiding(), 250);
        });
        mo.observe(document.body, { childList: true, subtree: true });
      }
    } catch (e) {
      // console.debug('[hide-soldout-variants] init error', e);
    }
  }

  // DOM hazır olunca çalıştır
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
